import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink } from "lucide-react";
import { COLPO_DI_CLACSON, IL_NOSTRO_GARAGE } from "@/data/site";

// Preview: backend URL assoluto da .env. Pacchetto statico: percorso relativo
// alla sottocartella di installazione (PUBLIC_URL), così le chiamate restano same-origin.
const API = process.env.REACT_APP_BACKEND_URL || process.env.PUBLIC_URL || "";

function YouTubeEmbed({ videoId, playlistId, thumbnail, aspect = "video", title }) {
  const [loaded, setLoaded] = useState(false);
  const src = playlistId
    ? `https://www.youtube-nocookie.com/embed/${videoId}?list=${playlistId}&rel=0&modestbranding=1&playsinline=1`
    : `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`;
  return (
    <div className={`relative w-full ${aspect === "video" ? "aspect-video" : "aspect-[16/9]"} overflow-hidden rounded-lg ring-1 ring-black/10 bg-black`}>
      {loaded ? (
        <iframe
          src={src + "&autoplay=1"}
          title={title}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="w-full h-full block"
        />
      ) : (
        <button
          type="button"
          onClick={() => setLoaded(true)}
          aria-label={`Riproduci: ${title}`}
          className="play-overlay group relative block w-full h-full cursor-pointer"
        >
          <img src={thumbnail} alt={title} className="w-full h-full object-cover" loading="lazy" />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-16 h-16 md:w-20 md:h-20 grid place-items-center rounded-full bg-[#FF6B1A] shadow-xl group-hover:scale-110 transition-transform duration-500">
            <Play size={28} fill="white" className="text-white translate-x-0.5" strokeWidth={0} />
          </span>
        </button>
      )}
    </div>
  );
}

function Proposal({ num, eyebrow, title, body, children }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
      data-testid={`proposal-${num}`}
      className="py-14 md:py-20 border-t border-black/[0.1] first:border-t-0 first:pt-0"
    >
      <div className="grid lg:grid-cols-12 gap-6 md:gap-10">
        <div className="lg:col-span-4">
          <div className="label-eyebrow mb-3 flex items-center gap-3">
            <span className="text-[#FF6B1A]">{num}</span>
            <span className="w-5 h-px bg-black/25" />
            <span>Proposta</span>
          </div>
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#121212]/50 mb-5">
            {eyebrow}
          </div>
          <h3 className="font-display text-4xl md:text-5xl lg:text-[3.4vw] tighter leading-[1.02] text-[#121212] font-normal">
            {title}
          </h3>
        </div>
        <div className="lg:col-span-8 lg:pl-6">
          <p className="text-[#121212]/75 text-lg leading-relaxed">{body}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Proposals() {
  const [playlistCounts, setPlaylistCounts] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/youtube/playlists`)
      .then((r) => r.json())
      .then((d) => { if (d && d.live && d.counts) setPlaylistCounts(d.counts); })
      .catch(() => {});
  }, []);

  const clacsonCount = playlistCounts?.clacson ?? COLPO_DI_CLACSON.videosCount;
  const garageCount = playlistCounts?.garage ?? IL_NOSTRO_GARAGE.videosCount;

  return (
    <section id="proposte" data-testid="proposals-section" className="relative py-20 md:py-32 bg-[#f7f5f0] border-t border-black/[0.06]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="lg:col-span-4">
            <div className="label-eyebrow mb-4 flex items-center gap-3">
              <span>II.</span>
              <span className="w-5 h-px bg-black/25" />
              <span>Le proposte</span>
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-display tighter text-[#121212] text-4xl md:text-5xl lg:text-[3.8vw] leading-[1.02] font-normal">
              Tre modi di raccontare
              <br />
              <span className="italic text-[#FF6B1A]">l'automotive</span> su Modo Motori.
            </h2>
          </div>
        </div>

        {/* PROPOSAL 1 — Colpo di Clacson */}
        <Proposal
          num="01"
          eyebrow="Il talk di approfondimento"
          title={<>Colpo di <span className="italic text-[#FF6B1A]">Clacson</span>.</>}
          body="Un confronto dinamico sui temi più rilevanti del mondo automotive, tra domande, approfondimenti e punti di vista, con il contributo di ospiti del settore. Un appuntamento su YouTube per raccontare attualità, tendenze e innovazione, mettendo a confronto esperienze e competenze."
        >
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8 text-[#121212]/70 text-[15px]">
            {[
              "Dialogo tra conduttrice, esperto e ospiti",
              "Approfondimenti sui temi del settore",
              "Linguaggio accessibile e taglio editoriale",
              "Distribuzione su YouTube e canali del gruppo",
            ].map((v) => (
              <li key={v} className="flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B1A] mt-2 shrink-0" />
                <span>{v}</span>
              </li>
            ))}
          </ul>
          <YouTubeEmbed
            videoId={COLPO_DI_CLACSON.featuredVideoId}
            playlistId={COLPO_DI_CLACSON.playlistId}
            thumbnail={COLPO_DI_CLACSON.thumbnail}
            title="Colpo di Clacson — Playlist"
          />
          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <span className="inline-flex items-center gap-2.5 text-[#121212] text-sm font-medium">
              <span className="w-7 h-7 rounded-full bg-[#FF6B1A]/10 flex items-center justify-center">
                <Play size={12} fill="#FF6B1A" className="text-[#FF6B1A] translate-x-px" strokeWidth={0} />
              </span>
              Playlist · {clacsonCount} contenuti
            </span>
            <a
              href={COLPO_DI_CLACSON.playlistUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 text-sm font-medium text-[#121212] hover:bg-[#FF6B1A] hover:border-[#FF6B1A] hover:text-white transition-colors duration-300"
            >
              Apri su YouTube <ExternalLink size={14} />
            </a>
          </div>
        </Proposal>

        {/* PROPOSAL 2 — Branded Content Video */}
        <Proposal
          num="02"
          eyebrow="Il tuo brand, al centro del racconto"
          title={<>Branded Content <span className="italic text-[#FF6B1A]">Video</span>.</>}
          body="Realizziamo contenuti dedicati ai prodotti, servizi e novità automotive, pensati per catturare l'attenzione del tuo pubblico e generare interesse verso la tua attività e il tuo brand."
        >
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8 text-[#121212]/70 text-[15px]">
            {[
              "Presentazioni di nuovi modelli, tecnologie e servizi",
              "Prove su strada ed esperienze di guida",
              "Interviste a manager, esperti e protagonisti del settore",
              "Reportage da eventi, lanci e anteprime",
              "Approfondimenti su mobilità elettrica, sicurezza e innovazione",
              "Pillole video per i social",
            ].map((v) => (
              <li key={v} className="flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B1A] mt-2 shrink-0" />
                <span>{v}</span>
              </li>
            ))}
          </ul>
          <p className="text-[#121212]/70 text-base leading-relaxed mb-8 italic">
            Dall'ideazione alle riprese, dal montaggio alla distribuzione: un progetto costruito intorno agli obiettivi del brand.
          </p>

          {/* Il Nostro Garage - example of editorial automotive production */}
          <div className="rounded-xl bg-white ring-1 ring-black/[0.08] p-5 md:p-6">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#121212]/50 mb-1">
                  Esempio di produzione editoriale
                </div>
                <h4 className="font-display text-xl md:text-2xl tighter text-[#121212]">
                  Il Nostro <span className="italic text-[#FF6B1A]">Garage</span>
                </h4>
                <p className="text-[#121212]/60 text-sm mt-1.5 max-w-xl">
                  Serie editoriale di schede modello: specifiche, allestimenti, pregi e difetti presentati in formato breve.
                </p>
              </div>
            </div>
            <YouTubeEmbed
              videoId={IL_NOSTRO_GARAGE.featuredVideoId}
              playlistId={IL_NOSTRO_GARAGE.playlistId}
              thumbnail={IL_NOSTRO_GARAGE.thumbnail}
              title="Il Nostro Garage — Playlist"
            />
            <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2.5 text-[#121212] text-sm font-medium">
                <span className="w-7 h-7 rounded-full bg-[#FF6B1A]/10 flex items-center justify-center">
                  <Play size={12} fill="#FF6B1A" className="text-[#FF6B1A] translate-x-px" strokeWidth={0} />
                </span>
                Playlist · {garageCount} schede
              </span>
              <a
                href={IL_NOSTRO_GARAGE.playlistUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 text-sm font-medium text-[#121212] hover:bg-[#FF6B1A] hover:border-[#FF6B1A] hover:text-white transition-colors duration-300"
              >
                Apri su YouTube <ExternalLink size={14} />
              </a>
            </div>
            <p className="mt-3 font-mono text-[10px] tracking-[0.14em] uppercase text-[#121212]/40">
              Produzione editoriale della redazione Modo Motori.
            </p>
          </div>
        </Proposal>

        {/* PROPOSAL 3 — Coming soon */}
        <Proposal
          num="03"
          eyebrow="Coming soon"
          title={<>Un nuovo format <span className="italic text-[#FF6B1A]">sta arrivando</span>.</>}
          body="Un nuovo modo di raccontare il mondo automotive. Presto tutti i dettagli."
        >
          <div className="relative rounded-xl bg-[#121212] text-white p-8 md:p-12 overflow-hidden">
            <div className="absolute top-5 right-5 chip">
              <span className="dot animate-pulse-dot" />
              Coming soon
            </div>
            <div className="relative z-10 max-w-xl">
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/50 mb-5">
                Teaser · Dettagli in arrivo
              </div>
              <p className="font-display text-2xl md:text-3xl leading-[1.1] text-white italic">
                "Qualcosa di nuovo si sta accendendo in redazione."
              </p>
              <p className="text-white/60 mt-5 text-sm">
                Il nome, il concept e la data di lancio verranno comunicati nelle prossime settimane.
              </p>
            </div>
          </div>
        </Proposal>
      </div>
    </section>
  );
}
